import { Box, Typography, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from 'react';

interface HorizontalBarChartProps {
  data: Array<{
    level_number: number;
    tier_number: number;
    avg_accuracy: number;
    avg_lh_accuracy: number;
    avg_rh_accuracy: number;
  }>;
}

type HandType = 'both' | 'left' | 'right';

export function HorizontalBarChart({ data }: HorizontalBarChartProps) {
  const [selectedHand, setSelectedHand] = useState<HandType>('both');

  const handleHandChange = (event: React.MouseEvent<HTMLElement>, newHand: HandType | null) => {
    if (newHand !== null) {
      setSelectedHand(newHand);
    }
  };

  type DataItem = HorizontalBarChartProps['data'][number];
  const getAccuracyValue = (item: DataItem) => {
    switch (selectedHand) {
      case 'left':
        return item.avg_lh_accuracy;
      case 'right':
        return item.avg_rh_accuracy;
      case 'both':
      default:
        return item.avg_accuracy;
    }
  };

  const bars = data.map((item, _index) => {
    const accuracy = getAccuracyValue(item);
    return {
      id: `${item.level_number}-${item.tier_number}-${selectedHand}`,
      width: `${accuracy}%`,
      label: `Level ${item.level_number}, Tier ${item.tier_number}`,
      accuracy: accuracy,
    };
  });

  return (
    <Paper
      sx={{
        backgroundColor: '#1f2937',
        color: '#ffffff',
        borderRadius: 2,
        padding: 3,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#ffffff',
            fontSize: '1.125rem',
            fontWeight: 500,
          }}
        >
          Latest Accuracy by Tier
        </Typography>

        <ToggleButtonGroup
          value={selectedHand}
          exclusive
          onChange={handleHandChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: '#9ca3af',
              borderColor: '#374151',
              padding: '4px 8px',
              fontSize: '0.75rem',
              minWidth: '28px',
              '&.Mui-selected': {
                backgroundColor: '#374151',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#4b5563',
                },
              },
              '&:hover': {
                backgroundColor: '#374151',
                borderColor: '#4b5563',
              },
            },
          }}
        >
          <ToggleButton value="left">L</ToggleButton>
          <ToggleButton value="both">B</ToggleButton>
          <ToggleButton value="right">R</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {bars.length > 0 ? (
          bars.map((bar, _index) => (
            <Box key={bar.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                  }}
                >
                  {bar.label}
                </Typography>
                <Typography
                  sx={{
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                  }}
                >
                  {Math.round(bar.accuracy)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#374151',
                  borderRadius: '9999px',
                  height: '12px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#22c55e',
                    height: '12px',
                    borderRadius: '9999px',
                    transition: 'all 300ms ease-out',
                    width: bar.width,
                  }}
                />
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              py: 4,
              opacity: 0.6,
            }}
          >
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #6b7280',
                  borderRadius: '4px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    width: '12px',
                    height: '8px',
                    backgroundColor: '#6b7280',
                    borderRadius: '2px',
                  },
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              No accuracy data available
            </Typography>
            <Typography
              sx={{
                color: '#4b5563',
                fontSize: '0.75rem',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              Complete some levels to see your accuracy stats
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
